import { ChangeEvent, useEffect, useMemo, useState, useRef } from 'react'
import { usePageLayout } from '../hooks/usePageLayout'
import { NavLink, useNavigate } from 'react-router-dom'
import './AssetsBrowser.css'
import { Locale } from '../components/Locale'
import {
  getObjects,
  GetObjectsOutput,
  getObjectStyleCount,
  getScenes,
  GetScenesOutput,
  getSceneStyleCount,
} from '../sdk/assetsBrowser'
import SimpleObjectViewer from '../components/SimpleObjectViewer'
import PaginationController from '../components/PaginationController'
import { useToken } from '../hooks/useToken'
import ComboBox from '../components/ComboBox'
import { useGet } from '../hooks/useGet'
import { getSkyboxStyleList } from '../sdk/skybox'
import { getSunoStyleList } from '../sdk/music'

function AssetsBrowser() {
  // setup Layout options
  const layoutOptions = useMemo(
    () => ({
      showSidebar: true,
      showHeader: true,
      showFooter: true,
      title: {
        en: 'Assets Browser',
        zh_cn: '资源浏览',
        zh_hk: '資源瀏覽',
      },
    }),
    [],
  )
  usePageLayout(layoutOptions)

  // types
  type Session = {
    scene?: boolean
    music?: boolean
    object?: boolean
  }

  // constants
  const skyboxStyleList = useGet([], () =>
    getSkyboxStyleList({}).then(json => json.styles),
  ).state

  const sunoStyleList = useGet([], () =>
    getSunoStyleList({}).then(json => json.styles),
  ).state

  let skybox_styleList = [
    { en: '(all scene)', zh_cn: '(全部场景)', zh_hk: '(全部場景)' },
  ]
  for (let i = 0; i < skyboxStyleList.length; i++) {
    skybox_styleList.push({
      en: skyboxStyleList[i].name,
      zh_cn: skyboxStyleList[i].name,
      zh_hk: skyboxStyleList[i].name,
    })
  }

  let suno_styleList = [
    { en: '(all music)', zh_cn: '(全部音乐)', zh_hk: '(全部音樂)' },
  ]
  for (let i = 0; i < sunoStyleList.length; i++) {
    suno_styleList.push({
      en: sunoStyleList[i],
      zh_cn: sunoStyleList[i],
      zh_hk: sunoStyleList[i],
    })
  }

  const [sceneStyleCounter, setSceneStyleCounter] = useState<
    Record<string, number>
  >({})
  const [musicStyleCounter, setMusicStyleCounter] = useState<
    Record<string, number>
  >({})
  const [objectStyleCounter, setObjectStyleCounter] = useState<
    Record<string, number>
  >({})

  const tripo_styleList = [
    {
      en: '(all object)',
      zh_cn: '(全部模型)',
      zh_hk: '(全部模型)',
    },
    {
      en: 'person:person2cartoon',
      zh_cn: '角色:卡通',
      zh_hk: '角色:卡通',
      preview_url: '/images/preview-person-cartoon.webp',
    },
    {
      en: 'object:clay',
      zh_cn: '物件:黏土',
      zh_hk: '物件:黏土',
      preview_url: '/images/preview-object-clay.webp',
    },
    {
      en: 'object:steampunk',
      zh_cn: '物件:蒸气庞克',
      zh_hk: '物件:蒸氣龐克',
      preview_url: '/images/preview-object-steampunk.webp',
    },
    {
      en: 'object:barbie',
      zh_cn: '物件:芭比',
      zh_hk: '物件:芭比',
      preview_url: '/images/preview-object-barbie.webp',
    },
    {
      en: 'object:christmas',
      zh_cn: '物件:圣诞',
      zh_hk: '物件:聖誕',
      preview_url: '/images/preview-object-christmas.webp',
    },
    {
      en: 'animal:venom',
      zh_cn: '生物:毒液',
      zh_hk: '生物:毒液',
      preview_url: '/images/preview-animal-venom.webp',
    },
    {
      en: 'gold',
      zh_cn: '金',
      zh_hk: '金',
      preview_url: '/images/preview-gold.webp',
    },
    {
      en: 'ancient_bronze',
      zh_cn: '古代青铜器',
      zh_hk: '古代青銅器',
      preview_url: '/images/preview-ancient-bronze.webp',
    },
  ]

  // hooks
  const navigate = useNavigate()
  const { token } = useToken()

  // useRefs
  const searchTimeoutRef = useRef<NodeJS.Timeout | null>(null)

  // useStates
  const [isSessionEnabled, setIsSessionEnabled] = useState<Session>({
    scene: true,
    music: true,
    object: true,
  })
  const [sceneMaxPage, setSceneMaxPage] = useState<number>(0)
  const [objectMaxPage, setObjectMaxPage] = useState<number>(0)
  const [sceneCurrentPage, setSceneCurrentPage] = useState<number>(1)
  const [objectCurrentPage, setObjectCurrentPage] = useState<number>(1)
  const updateIsSessionEnabled = (session: Session) =>
    setIsSessionEnabled(prev => {
      const updatedSession = { ...prev, ...session }
      const hasAnyEnabled = Object.values(updatedSession).some(
        value => value === true,
      )
      if (!hasAnyEnabled) {
        return prev
      }
      return updatedSession
    })
  const [isReadyOnly, setIsReadyOnly] = useState<boolean>(false)
  const [isBookmarked, setIsBookmarked] = useState<boolean>(false)
  const [isMyAssets, setIsMyAssets] = useState<boolean>(false)
  const [inputValue, setInputValue] = useState<string>('')
  const [searchValue, setSearchValue] = useState<string>('')
  const [scenes, setScenes] = useState<GetScenesOutput['scenes'] | null>(null)
  const [music, setMusic] = useState<{ text: string } | null>(null)
  const [objects, setObjects] = useState<GetObjectsOutput['objects'] | null>(
    null,
  )
  const [skyboxStyle, setSkyboxStyle] = useState<string>(
    skybox_styleList[0]['en'],
  )
  const [sunoStyle, setSunoStyle] = useState<string>(suno_styleList[0]['en'])
  const [tripoStyle, setTripoStyle] = useState<string>(tripo_styleList[0]['en'])

  // functions
  const handleOnSearchChange = (event: ChangeEvent<HTMLInputElement>) => {
    const value = event.target.value
    setInputValue(value.toLowerCase())

    if (searchTimeoutRef.current) {
      clearTimeout(searchTimeoutRef.current)
    }

    searchTimeoutRef.current = setTimeout(() => {
      setSearchValue(value)
    }, 500)
  }

  const loadAssets = async () => {
    try {
      await getScenes({
        query: {
          is_ready_only: isReadyOnly,
          is_bookmarked: isBookmarked,
          is_user_created: isMyAssets,
          page_number: sceneCurrentPage,
          items_per_page: 6,
          search_keywords: searchValue,
          skybox_style:
            skyboxStyle === `${skybox_styleList[0]['en']}` ? '%%' : skyboxStyle,
          suno_style:
            sunoStyle === `${suno_styleList[0]['en']}` ? '%%' : sunoStyle,
        },
      }).then(res => {
        if (res.error) throw new Error(res.error)
        setScenes(res.scenes)
        setSceneCurrentPage(res.page_number)
        setSceneMaxPage(res.maxPage)
      })

      await getObjects({
        query: {
          is_bookmarked: isBookmarked,
          is_user_created: isMyAssets,
          page_number: objectCurrentPage,
          items_per_page: 8,
          search_keywords: searchValue,
          tripo_style:
            tripoStyle === `${tripo_styleList[0]['en']}` ? '%%' : tripoStyle,
        },
      }).then(res => {
        if (res.error) throw new Error(res.error)
        setObjects(res.objects)
        setObjectCurrentPage(res.page_number)
        setObjectMaxPage(res.maxPage)
      })
    } catch (error) {
      console.error('Error loading assets:', error)
    }
  }

  const loadSceneStyleCount = async () => {
    try {
      await getSceneStyleCount({
        query: {
          is_bookmarked: isBookmarked,
          is_user_created: isMyAssets,
          search_keywords: searchValue,
          scene_style: tripoStyle,
          music_style: sunoStyle,
        },
      }).then(res => {
        if (res.error) throw new Error(res.error)
        console.log(res.sceneStyleCounter)
        console.log(res.musicStyleCounter)
        setSceneStyleCounter(res.sceneStyleCounter)
        setMusicStyleCounter(res.musicStyleCounter)
      })
    } catch (error) {
      console.error('Error loading scene style count:', error)
    }
  }

  const loadObjectStyleCount = async () => {
    try {
      await getObjectStyleCount({
        query: {
          is_bookmarked: isBookmarked,
          is_user_created: isMyAssets,
          search_keywords: searchValue,
        },
      }).then(res => {
        if (res.error) throw new Error(res.error)
        setObjectStyleCounter(res.styleCounter)
      })
    } catch (error) {
      console.error('Error loading object style count:', error)
    }
  }

  // useEffects
  useEffect(() => {
    loadAssets()
    loadSceneStyleCount()
    loadObjectStyleCount()
  }, [
    isReadyOnly,
    isBookmarked,
    isMyAssets,
    sceneCurrentPage,
    objectCurrentPage,
    searchValue,
    skyboxStyle,
    sunoStyle,
    tripoStyle,
  ])

  useEffect(() => {
    return () => {
      if (searchTimeoutRef.current) {
        clearTimeout(searchTimeoutRef.current)
      }
    }
  }, [])

  return (
    <>
      <div className="page-assets">
        <div className="query card">
          <div className="search">
            <input
              className="search-bar"
              placeholder="Enter to search"
              value={inputValue}
              onChange={handleOnSearchChange}
            />
          </div>
          <div className="filter">
            <div className="filter-checkbox">
              <div className="session-checkbox">
                <input
                  type="checkbox"
                  checked={isSessionEnabled.scene}
                  onChange={e =>
                    updateIsSessionEnabled({ scene: e.target.checked })
                  }
                />
                <Locale en="Scenes" zh_cn="场景" zh_hk="場景" />
              </div>
              <div className="session-checkbox">
                <input
                  type="checkbox"
                  checked={isSessionEnabled.music}
                  onChange={e =>
                    updateIsSessionEnabled({ music: e.target.checked })
                  }
                />
                <Locale en="Music" zh_cn="音乐" zh_hk="音樂" />
              </div>
              <div className="session-checkbox">
                <input
                  type="checkbox"
                  checked={isSessionEnabled.object}
                  onChange={e =>
                    updateIsSessionEnabled({ object: e.target.checked })
                  }
                />
                <Locale en="3D Models" zh_cn="3D 模型" zh_hk="3D 模型" />
              </div>
              <div className="divisor" />
              {token && (
                <>
                  <div className="session-checkbox">
                    <input
                      type="checkbox"
                      checked={isReadyOnly}
                      onChange={e => setIsReadyOnly(e.target.checked)}
                    />
                    <Locale en="Generated" zh_cn="已生成" zh_hk="已生成" />
                  </div>
                  <div className="session-checkbox">
                    <input
                      type="checkbox"
                      checked={isBookmarked}
                      onChange={e => setIsBookmarked(e.target.checked)}
                    />
                    <Locale en="Bookmarked" zh_cn="已收藏" zh_hk="已收藏" />
                  </div>
                  <div className="session-checkbox">
                    <input
                      type="checkbox"
                      checked={isMyAssets}
                      onChange={e => setIsMyAssets(e.target.checked)}
                    />
                    <Locale en="My Assets" zh_cn="我的资源" zh_hk="我的資源" />
                  </div>

                  <div className="divisor" />
                </>
              )}
            </div>
            <div className="filter-combobox">
              <Locale en="Style" zh_cn="风格" zh_hk="風格" />
              {`:`}
              {isSessionEnabled.scene && (
                <ComboBox
                  className="skybox-combobox"
                  data={skybox_styleList}
                  styleCounter={sceneStyleCounter}
                  value={skyboxStyle}
                  onChange={option => setSkyboxStyle(option.en)}
                />
              )}
              {isSessionEnabled.music && (
                <ComboBox
                  className="suno-combobox"
                  data={suno_styleList}
                  styleCounter={musicStyleCounter}
                  value={sunoStyle}
                  onChange={option => setSunoStyle(option.en)}
                />
              )}
              {isSessionEnabled.object && (
                <ComboBox
                  className="tripo-combobox"
                  data={tripo_styleList}
                  styleCounter={objectStyleCounter}
                  value={tripoStyle}
                  onChange={option => setTripoStyle(option.en)}
                />
              )}
            </div>
          </div>
        </div>

        {isSessionEnabled.scene && (
          <div className="session sceneAssets">
            <div className="session-title">
              <Locale en="Scenes" zh_cn="场景" zh_hk="場景" />
            </div>
            <div className="session-result scene-result">
              {scenes &&
                scenes.map(scene => (
                  <div
                    className="session-item card card-hover"
                    key={scene.id}
                    onClick={() => navigate(`/editor/${scene.id}`)}
                  >
                    {scene.image_url ? (
                      <img src={scene.image_url} alt={scene.image_prompt} />
                    ) : (
                      <span className="loading-container loading-image">
                        Loading...
                      </span>
                    )}
                    <div className="description">
                      {scene.image_prompt && scene.image_style && (
                        <>
                          <span>
                            <i className="fa-solid fa-image" />
                            <p>{scene.image_prompt} </p>
                          </span>
                          <span>
                            <i className="fa-solid fa-image" />
                            {scene.image_style}
                          </span>
                        </>
                      )}
                    </div>
                  </div>
                ))}
            </div>
            <div className="session-pagination">
              <PaginationController
                currentPage={sceneCurrentPage}
                setCurrentPage={setSceneCurrentPage}
                maxPage={sceneMaxPage}
              />
            </div>
          </div>
        )}
        {isSessionEnabled.music && (
          <div className="session sceneAssets">
            <div className="session-title">
              <Locale en="Music" zh_cn="音乐" zh_hk="音樂" />
            </div>
            <div className="session-result scene-result">
              {true &&
                [{ name: 'music1' }, { name: 'music2' }].map(m => (
                  <div>{m.name}</div>
                ))}
              {}
              {/* {scenes &&
                scenes.map(scene => (
                  <div
                    className="session-item card card-hover"
                    key={scene.id}
                    onClick={() => navigate(`/editor/${scene.id}`)}
                  >
                    <img src={scene.image_url} alt={scene.image_prompt} />
                    <div className="description">
                      {scene.image_prompt && scene.image_style && (
                        <>
                          <span>
                            <i className="fa-solid fa-image" />
                            {scene.image_prompt}
                          </span>
                          <span>
                            <i className="fa-solid fa-image" />
                            {scene.image_style}
                          </span>
                        </>
                      )}
                      {scene.music_prompt && scene.music_style && (
                        <>
                          <span>
                            <i className="fa-solid fa-music" />
                            {scene.music_prompt}
                          </span>
                          <span>
                            <i className="fa-solid fa-music" />
                            {scene.music_style}
                          </span>
                        </>
                      )}
                    </div>
                  </div>
                ))} */}
            </div>
            <div className="session-pagination">
              <PaginationController
                currentPage={sceneCurrentPage}
                setCurrentPage={setSceneCurrentPage}
                maxPage={sceneMaxPage}
              />
            </div>
          </div>
        )}
        {isSessionEnabled.object && (
          <div className="session objectAssets">
            <div className="session-title">
              <Locale en="3D Models" zh_cn="3D 模型" zh_hk="3D 模型" />
            </div>
            <div
              className="session-result object-result"
              onMouseDown={e => {
                let target = e.target as HTMLElement
                let item = target.closest('.session-item')
                e.currentTarget
                  .querySelectorAll('.session-item')
                  .forEach(e => e.classList.remove('active'))
                console.log({ target, item })
                if (item) {
                  item.classList.add('active')
                }
              }}
            >
              {objects &&
                objects.map(object => (
                  <div className="session-item card card-hover" key={object.id}>
                    {object.preview_url && object.model_url ? (
                      <SimpleObjectViewer
                        preview_url={object.preview_url}
                        model_url={object.model_url}
                      />
                    ) : (
                      <span className="loading-container loading-object">
                        Loading...
                      </span>
                    )}
                    <div className="prompt-placeholder">{object.prompt}</div>
                    <div className="description">
                      {object.style && (
                        <>
                          <span>
                            <i className="fa-solid fa-image" />
                            {object.style}
                          </span>
                        </>
                      )}
                    </div>
                    {/* use nav link instead of button to support open in new tab */}
                    <NavLink
                      className="primary-button"
                      to={`/tripo3d?id=${object.id}`}
                    >
                      View Asset
                    </NavLink>
                  </div>
                ))}
            </div>
            <div className="session-pagination">
              <PaginationController
                currentPage={objectCurrentPage}
                setCurrentPage={setObjectCurrentPage}
                maxPage={objectMaxPage}
              />
            </div>
          </div>
        )}
      </div>
    </>
  )
}

export default AssetsBrowser
